import { useState } from 'react'
import { useBoolean, useSetState } from 'react-use'

import { IAddAddressBody, IAddPaymentMethodBody } from '@makyoapp/makyo-sdk/api/Organizations'
import { AddressAdapter, getCardTypeByValue, initialInputState, PaymentCardType } from '@makyoapp/makyo-common'

import { useGetPaymentMethodList } from '@/resolvers/org/billing/query'
import { AddressFormState, getInitialAddressState, isDisableAddressFormButton } from '@/components'

import { FormState, initialFormState } from '../PaymentMethodsPage'

const useAddPayment = (
  addresses: AddressAdapter[],
  onSubmit: (paymentMethod: IAddPaymentMethodBody, closeModal: () => void) => void,
  onClose?: () => void
) => {
  // Get Payment Methods
  const paymentMethodsQuery = useGetPaymentMethodList()
  const paymentMethods = paymentMethodsQuery.data || []

  const defaultState: FormState = {
    ...initialFormState,
    addressId: {
      ...initialInputState,
      disableButton: false,
      value: addresses.length > 0 ? addresses[0].id : '#',
    },
  }

  const [state, setState] = useSetState(defaultState)
  const defaultAddress = getInitialAddressState()
  const [addressState, setAddressState] = useSetState<AddressFormState>(defaultAddress)

  const [cardType, setCardType] = useState<PaymentCardType | undefined>(undefined)

  const [isLoading, setIsLoading] = useBoolean(false)

  const saveButtonDisabled =
    state.addressId.value !== '#'
      ? [
          state.card.disableButton,
          state.cvc.disableButton,
          state.expiry.disableButton,
          state.name.disableButton,
          state.addressId.disableButton,
        ].some((v) => v)
      : [
          state.card.disableButton,
          state.cvc.disableButton,
          state.expiry.disableButton,
          state.name.disableButton,
          isDisableAddressFormButton(addressState),
        ].some((v) => v)

  const closeModal = () => {
    setState(defaultState)
    setAddressState(defaultAddress)
    setCardType(undefined)
    setIsLoading(false)
    onClose?.()
  }

  const handleSavePayment = () => {
    setIsLoading(true)
    const exp = state.expiry.value.split('/')
    const addressForm: IAddAddressBody | undefined =
      state.addressId.value === '#'
        ? {
            _Name: addressState.title.value,
            _Line1: addressState.addressLine1.value,
            _Line2: addressState.addressLine2 || '',
            _PostalCode: addressState.zip.value,
            _City: addressState.city.value,
            _Country: addressState.country.value,
            _State: addressState.state.value,
            _PhoneNumber: addressState.phone || '',
            _Default: paymentMethods.length < 1,
          }
        : undefined
    onSubmit(
      {
        _CardNumber: state.card.value,
        _CVC: state.cvc.value,
        _AddressID: state.addressId.value === '#' ? '' : state.addressId.value,
        _ExpMonth: Number(exp[0].trim()),
        _ExpYear: Number(exp[1].trim()),
        _Brand: getCardTypeByValue(state.card.value).type,
        _Name: state.name.value,
        _Default: paymentMethods.length < 1,
        // TODO: remove any if sdk updated
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        _Address: addressForm as any,
      },
      closeModal
    )
  }

  return {
    state,
    cardType,
    addressState,
    isLoading,
    saveButtonDisabled,
    setState,
    setCardType,
    setAddressState,
    handleSavePayment,
    closeModal,
  }
}

export default useAddPayment
